import Vue from "vue";
import Vuex, { Store } from "vuex";
import { choice } from "@/wbcontroller/util";
import { WBState } from "./stateInterfaceType";
import { AllState } from "./AllState";
import {
  makeToolConfigModule,
  RenderConfig,
  ToolConfigMutations,
} from "@/store/modules/toolConfig";
import { getInitialState } from "./initialState";
import WhiteboardConfig from "@/WhiteboardConfig";
import mutations from "./mutations";
import getters from "./getters";
import {
  ARTISANAL_COLORS,
  FACTORY_COLORS,
  UTIL_COLORS,
} from "@/wbcontroller/COLORS";
import { defaultToolTypes } from "@/toolHUDItems";
import ToolType from "@/wbcontroller/types/ToolType";
import actions from "./actions";
import { isInIframe } from "@/wbcontroller/util";
import { makeUserModule } from "@/store/modules/user";
import { makeReportedModule } from "./modules/reported";
import { makeModalModule } from "./modules/modal";

Vue.use(Vuex);

export function makeStore(
  config: WhiteboardConfig,
  urlColors: string[] | null = null,
  urlStrokeWidths: number[] | null = null,
  urlToolTypes: ToolType[] | null = null,
  isZoomAllowed = true,
  renderConfig: RenderConfig | null = null
): Store<AllState> {
  const defaultColors = {
    artisanal: ARTISANAL_COLORS,
    util: UTIL_COLORS,
    factory: FACTORY_COLORS,
  };
  const defaultStrokeWidths = [1, 5, 10, 20, 30];
  const colorMap = defaultColors;
  let initialStrokeColor = choice(colorMap.artisanal, Math.random());
  if (urlColors) {
    initialStrokeColor = choice(urlColors, Math.random());
  }
  const toolTypes =
    urlToolTypes ||
    // Filter image upload out of default tool types if in iframe
    defaultToolTypes.filter(
      (t) =>
        !(isInIframe() || config.whiteboard.wbid === "demo") ||
        t !== ToolType.ImageUpload
    );
  const maybeInitialTool = localStorage.lastToolSelected || ToolType.Pen;
  let initialTool = toolTypes[0];
  if (!toolTypes || toolTypes.indexOf(maybeInitialTool) >= 0) {
    initialTool = maybeInitialTool;
  }
  let initialStickyColor =
    colorMap.artisanal.length > 10
      ? colorMap.artisanal[10]
      : colorMap.artisanal[0];
  if (urlColors) {
    initialStickyColor = choice(urlColors, Math.random());
  }
  const strokeWidths = urlStrokeWidths || defaultStrokeWidths;
  const initialStrokeWidth = strokeWidths[1];
  const store = new Vuex.Store<WBState>({
    state: getInitialState(
      config,
      initialStrokeColor,
      initialStrokeWidth,
      initialStickyColor,
      initialTool
    ),
    mutations,
    getters,
    actions,
    modules: {
      modal: makeModalModule(config),
      reported: makeReportedModule(isZoomAllowed),
      toolConfig: makeToolConfigModule(
        colorMap,
        strokeWidths,
        toolTypes,
        renderConfig
      ),
      user: makeUserModule(config),
    },
  }) as Store<AllState>;
  if (urlColors) {
    ToolConfigMutations.configureCustomColors(store.commit, [["", urlColors]]);
  }
  return store;
}
